import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { isNumber } from 'utils';
import { useItemsToShow, useSelectorProps } from 'hooks';
import {
  getCurrenciesPairInfoForHooks,
  getLocationParamsValuesForHooks,
  getCurrencyRatesIsFetching,
} from 'selectors';

import DropToggle from 'components/DropToggle';

import ContentPlaceholder from 'components/ContentPlaceholder';
import CopyButton from 'components/CopyButton';
import Table from 'components/ConvertorTable';

import { Wrapper, Amount } from './styles';

const ITEMS_COUNT = 8;

const COLUMN_IDS = ['date', 'rate', 'amount'];

const RatesTable = memo(({ rates }) => {
  const { formatMessage, formatNumber } = useIntl();
  const selectorProps = useSelectorProps();
  const { sourceValue } = useSelector(state =>
    getLocationParamsValuesForHooks(state, selectorProps)
  );
  const { source, target } = useSelector(state =>
    getCurrenciesPairInfoForHooks(state, selectorProps)
  );
  const isFetching = useSelector(getCurrencyRatesIsFetching);

  const { isShow, toggle, items } = useItemsToShow(rates, ITEMS_COUNT);

  const columns = useMemo(
    () =>
      COLUMN_IDS.map(id => ({
        id,
        children:
          id !== 'date'
            ? formatMessage(
                {
                  id: `rates.${id}.col.title`,
                },
                {
                  value: formatNumber(sourceValue, {
                    maximumFractionDigits: source.type === 'crypto' ? 8 : 2,
                  }),
                  sourceCode: source.code,
                  targetCode: target.code,
                }
              )
            : null,
      })),
    [source, target, sourceValue]
  );

  const rows = useMemo(
    () =>
      items.map(({ id, rate, summ, type, timestamp }) => {
        const roundedSumm = isNumber(summ)
          ? formatNumber(summ, {
              maximumFractionDigits: type === 'crypto' ? 8 : 2,
            })
          : '-';

        return {
          id,
          ceils: [
            {
              key: `date-${id}`,
              children: !isFetching ? (
                timestamp.toString()
              ) : (
                <ContentPlaceholder style={{ width: '58px', height: '20px' }} />
              ),
              props: {
                style: {
                  width: '40%',
                  textAlign: 'left',
                },
              },
            },
            {
              key: `rate-${id}`,
              children: !isFetching ? (
                <>{isNumber(rate) ? formatNumber(rate) : '-'}</>
              ) : (
                <ContentPlaceholder style={{ width: '58px', height: '20px' }} />
              ),
            },
            {
              key: `amount-${id}`,
              children: !isFetching ? (
                <Amount>
                  {isNumber(summ) ? (
                    <CopyButton
                      textForCopy="hello"
                      summ={roundedSumm}
                      currency={target.code}
                    />
                  ) : (
                    '-'
                  )}
                </Amount>
              ) : (
                <ContentPlaceholder
                  style={{ width: '146px', height: '20px' }}
                />
              ),
            },
          ],
        };
      }),
    [items, target]
  );

  return (
    <Wrapper>
      <Table columns={columns} rows={rows} />

      {rates.length > ITEMS_COUNT && (
        <DropToggle
          onPress={toggle}
          isOpen={isShow}
          openTitle="show.all.button"
          closeTitle="hide.button"
        />
      )}
    </Wrapper>
  );
});

RatesTable.propTypes = {
  rates: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default RatesTable;
